import { useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { EnvelopeIcon } from '@heroicons/react/24/solid';
import Sidebar from './Sidebar';
import ThemeToggle from '../common/ThemeToggle';
import NotificationBell from '../common/NotificationBell';
import Button from '../common/Button';
import { useAuth } from '../../context/AuthContext';
import { NotificationProvider } from '../../context/NotificationContext';
import { authAPI } from '../../services/api';

function EmailVerificationGate({ onLogout }) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try {
      await authAPI.resendVerification();
      setResent(true);
    } catch {
      // Silently fail - user can retry
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
      <div className="w-full max-w-md text-center space-y-6">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
            <EnvelopeIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Verify your email address
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          We've sent a verification link to your email. Please check your inbox and click the link to activate your account.
        </p>
        {resent ? (
          <p className="text-sm text-green-600 dark:text-green-400 font-medium">
            Verification email sent! Check your inbox.
          </p>
        ) : (
          <Button onClick={handleResend} loading={resending} variant="outline" className="w-full">
            Resend verification email
          </Button>
        )}
        <button
          onClick={onLogout}
          className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, isAuthenticated, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.emailVerified) {
    return <EmailVerificationGate onLogout={logout} />;
  }

  return (
    <NotificationProvider>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        {/* Mobile sidebar */}
        <Dialog as="div" className="lg:hidden" open={sidebarOpen} onClose={setSidebarOpen}>
        <div className="fixed inset-0 z-50 bg-slate-900/50" />
        <Dialog.Panel className="fixed inset-y-0 left-0 z-50 w-72">
          <div className="absolute right-0 top-0 -mr-12 pt-4">
            <button
              type="button"
              className="ml-1 flex h-10 w-10 items-center justify-center rounded-full"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <Sidebar mobile onClose={() => setSidebarOpen(false)} />
        </Dialog.Panel>
      </Dialog>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <Sidebar />
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 items-center gap-x-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            className="lg:hidden p-2 text-slate-700 dark:text-slate-200"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex-1" />

          <NotificationBell />
          <ThemeToggle />
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
    </NotificationProvider>
  );
}
